/**
 * markers.js — Shared BPMN marker SVG renderers for tasks and sub-processes.
 *
 * Renders loop, sequential multi-instance, and parallel multi-instance markers
 * at the bottom center of an element.
 *
 * Usage in a shape's getShape():
 *   import { renderMarkers } from './markers';
 *   // inside getShape(), append the result to the SVG group children:
 *   ...renderMarkers(x, y, width, height, model.properties, style.stroke)
 */

import { h } from '@logicflow/core';

/**
 * Returns an array of h() SVG elements representing BPMN activity markers.
 *
 * @param {number} x      - center x of the shape
 * @param {number} y      - center y of the shape
 * @param {number} width  - width of the shape
 * @param {number} height - height of the shape
 * @param {object} props  - element properties (loopType expected)
 * @param {string} stroke - stroke color for markers
 * @returns {Array} array of h() elements (may be empty)
 */
export function renderMarkers(x, y, width, height, props, stroke) {
  const loopType = props && props.loopType;
  if (!loopType || loopType === 'None') return [];

  const bottomY = y + height / 2 - 6;
  const sw = 1.4;

  switch (loopType) {
    case 'Loop':
      return [renderLoopMarker(x, bottomY, stroke, sw)];
    case 'SequentialMultiInstance':
      return [renderSequentialMI(x, bottomY, stroke, sw)];
    case 'ParallelMultiInstance':
      return [renderParallelMI(x, bottomY, stroke, sw)];
    default:
      return [];
  }
}

/**
 * Standard loop marker: circular arrow at bottom center.
 */
function renderLoopMarker(cx, cy, stroke, sw) {
  // Small circular arrow: arc + arrowhead
  return h('g', { key: 'marker-loop' }, [
    h('path', {
      d: `M ${cx + 4} ${cy} A 4.5 4.5 0 1 0 ${cx} ${cy + 5}`,
      fill: 'none',
      stroke,
      strokeWidth: sw,
    }),
    // Arrowhead at the end of the arc
    h('path', {
      d: `M ${cx - 2.5} ${cy + 3} L ${cx} ${cy + 5.5} L ${cx + 2.5} ${cy + 3}`,
      fill: 'none',
      stroke,
      strokeWidth: sw,
    }),
  ]);
}

/**
 * Sequential multi-instance marker: three horizontal lines (≡).
 */
function renderSequentialMI(cx, cy, stroke, sw) {
  const w = 8;
  const gap = 3;
  return h('g', { key: 'marker-seq-mi' }, [
    h('line', { x1: cx - w / 2, y1: cy, x2: cx + w / 2, y2: cy, stroke, strokeWidth: sw }),
    h('line', { x1: cx - w / 2, y1: cy + gap, x2: cx + w / 2, y2: cy + gap, stroke, strokeWidth: sw }),
    h('line', { x1: cx - w / 2, y1: cy + gap * 2, x2: cx + w / 2, y2: cy + gap * 2, stroke, strokeWidth: sw }),
  ]);
}

/**
 * Parallel multi-instance marker: three vertical lines (|||).
 */
function renderParallelMI(cx, cy, stroke, sw) {
  const h2 = 8;
  const gap = 3;
  return h('g', { key: 'marker-par-mi' }, [
    h('line', { x1: cx - gap, y1: cy, x2: cx - gap, y2: cy + h2, stroke, strokeWidth: sw }),
    h('line', { x1: cx, y1: cy, x2: cx, y2: cy + h2, stroke, strokeWidth: sw }),
    h('line', { x1: cx + gap, y1: cy, x2: cx + gap, y2: cy + h2, stroke, strokeWidth: sw }),
  ]);
}
